import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';

const NAV_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/solutions', label: 'Solutions' },
  { to: '/projects', label: 'Projects', match: (path) => path.startsWith('/project') },
  { to: '/news', label: 'News' },
  { to: '/about', label: 'About' },
];

const Navbar = () => {
  const location = useLocation();
  const menuRef = useRef(null);
  const linkRefs = useRef(new Map());
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [hoveredTo, setHoveredTo] = useState(null);
  const [indicator, setIndicator] = useState({ left: 0, top: 0, width: 0, height: 0, opacity: 0 });

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((open) => !open);

  const setLinkRef = (to) => (element) => {
    if (element) {
      linkRefs.current.set(to, element);
    } else {
      linkRefs.current.delete(to);
    }
  };

  const getActiveTo = useCallback(() => {
    const matched = NAV_LINKS.find(({ to, end, match }) => {
      if (match) {
        return match(location.pathname);
      }
      if (end) {
        return location.pathname === to;
      }
      return location.pathname === to || location.pathname.startsWith(`${to}/`);
    });

    return matched?.to ?? null;
  }, [location.pathname]);

  const updateIndicator = useCallback(
    (targetTo) => {
      const menu = menuRef.current;
      if (!menu || window.innerWidth <= 1024) {
        setIndicator((prev) => ({ ...prev, opacity: 0 }));
        return;
      }

      const activeTo = targetTo ?? hoveredTo ?? getActiveTo();
      const target = activeTo ? linkRefs.current.get(activeTo) : null;

      if (!target) {
        setIndicator((prev) => ({ ...prev, opacity: 0 }));
        return;
      }

      const menuRect = menu.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      setIndicator({
        left: targetRect.left - menuRect.left,
        top: targetRect.top - menuRect.top,
        width: targetRect.width,
        height: targetRect.height,
        opacity: 1,
      });
    },
    [getActiveTo, hoveredTo]
  );

  useLayoutEffect(() => {
    updateIndicator();
  }, [location.pathname, hoveredTo, updateIndicator]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleResize = () => updateIndicator();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateIndicator]);

  const handleLinkEnter = (to) => setHoveredTo(to);
  const handleLinkLeave = () => setHoveredTo(null);

  return (
    <>
      <header className="vt-header">
        <div className="vt-header__inner">
          <Link to="/" className="vt-header__logo" onClick={closeMenu}>
            <img src="/images/logo/logoedite.png" alt="Vientiane Traffic" />
          </Link>

          <button
            type="button"
            className={`vt-header__toggle ${isMenuOpen ? 'is-open' : ''}`}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="vt-primary-nav"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span />
            <span />
            <span />
          </button>

          <div
            id="vt-primary-nav"
            className={`vt-header__menu ${isMenuOpen ? 'is-open' : ''}`}
          >
            <nav ref={menuRef} className="vt-header__nav" aria-label="Primary">
              <span
                className="vt-header__indicator"
                style={{
                  left: `${indicator.left}px`,
                  top: `${indicator.top}px`,
                  width: `${indicator.width}px`,
                  height: `${indicator.height}px`,
                  opacity: indicator.opacity,
                }}
                aria-hidden="true"
              />

              <ul className="vt-header__list">
                {NAV_LINKS.map(({ to, label, end, match }, index) => (
                  <li
                    key={to}
                    className="vt-header__item"
                    style={{ '--nav-delay': `${index * 0.06 + 0.05}s` }}
                  >
                    <NavLink
                      ref={setLinkRef(to)}
                      to={to}
                      end={end}
                      className={({ isActive }) => {
                        const active = match ? match(location.pathname) : isActive;
                        return `vt-header__link${active ? ' is-active' : ''}`;
                      }}
                      onClick={closeMenu}
                      onMouseEnter={() => handleLinkEnter(to)}
                      onMouseLeave={handleLinkLeave}
                      onFocus={() => handleLinkEnter(to)}
                      onBlur={handleLinkLeave}
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            <NavLink
              to="/contact"
              className={({ isActive }) => `vt-header__cta${isActive ? ' is-active' : ''}`}
              onClick={closeMenu}
            >
              Contact us
            </NavLink>
          </div>
        </div>

        <button
          type="button"
          className={`vt-header__backdrop ${isMenuOpen ? 'is-visible' : ''}`}
          onClick={closeMenu}
          aria-label="Close menu"
          tabIndex={isMenuOpen ? 0 : -1}
        />
      </header>
      <div className="vt-header__spacer" aria-hidden="true" />
    </>
  );
};

export default Navbar;
